<script setup lang="ts">
   import AppTopBar from '../components/LmsMainNav.vue';
   import WebsiteFooter from '~/components/WebsiteFooter.vue';
   import useAuthStore from './../stores/auth';
   import useUiStore from '~/stores/ui';
   import { storeToRefs } from '#imports';
   import { LmsRouteNames } from '~/types/types';
   import Dialog from 'primevue/dialog';
   import { useQueryClient } from '@tanstack/vue-query';
   import { onMounted } from 'vue';
   import axios from 'axios';
   import { useNavOffset } from '~/composables/useNavOffset';

   // Hide the entire layout during Auth0 callback to prevent nav/layout flash
   // before the auth0 plugin redirects to the final destination.
   const route = useRoute();
   const isAuth0Callback = computed(() => !!route.query.code);

   //store
   const authStore = useAuthStore();
   const { user } = storeToRefs(authStore);
   const uiStore = useUiStore();
   const { isSubscriptionCtaModalOpen, subscriptionCtaContext } = storeToRefs(uiStore);

   // Track nav height for sticky positioning in child components
   useNavOffset();

   // Dynamic content based on context
   const modalContent = computed(() => {
      if (subscriptionCtaContext.value === 'blog') {
         return {
            title: 'Premium Content',
            message: 'Get access to this and many other premium blog posts by purchasing a monthly or yearly subscription.',
            buttonText: 'Get a subscription'
         };
      }

      // Default to course content
      return {
         title: 'Included in your monthly or yearly subscription',
         message: 'Get access to this and many other courses to level up your polo skills by purchasing a monthly or yearly subscription.',
         buttonText: 'Get a subscription'
      };
   });

   // Prefetch sitewide subscriptions in background for instant subscribe page loads
   const queryClient = useQueryClient();

   onMounted(() => {
      // Only prefetch if authenticated
      if (!authStore.accessToken) {
         return;
      }

      // Use requestIdleCallback to wait for idle time (non-blocking)
      // Falls back to setTimeout with delay if not supported
      const prefetchWhenIdle = async () => {
         try {
            await queryClient.prefetchQuery({
               queryKey: ['userSubscriptions', 'sitewide'],
               queryFn: async () => {
                  const response = await axios.post(
                     `${authStore.lmsApiBaseurl}user-subscriptions`,
                     { type: 'sitewide' },
                     {
                        headers: authStore.authHeaders,
                     }
                  );
                  // Return full response including active subscriptions
                  return {
                     active: response.data.active || [],
                     purchased_bundles: response.data.purchased_bundles || [],
                     purchasable: response.data.purchasable || [],
                  };
               },
               staleTime: 5 * 60 * 1000,
            });
         } catch (error) {
            // Silent failure - page will fetch on demand if prefetch fails
         }
      };

      // Use browser idle time to avoid competing with critical resources
      if ('requestIdleCallback' in window) {
         requestIdleCallback(prefetchWhenIdle, { timeout: 2000 });
      } else {
         // Fallback: delay slightly to let critical page resources load first
         setTimeout(prefetchWhenIdle, 300);
      }
   });

</script>

<template>
   <!-- Show spinner during Auth0 callback to prevent layout flash -->
   <div v-if="isAuth0Callback" class="flex h-screen w-full items-center justify-center">
      <ProgressSpinner />
   </div>
   <div v-else class="font-sans text-gray-700">
      <AppTopBar />
      <div
         class="mx-auto flex min-h-screen w-full max-w-lmsCenteredContainer flex-col px-5 pt-28 sm:px-6 md:pt-36 lg:px-8">
         <slot>
            <NuxtPage />
         </slot>
         <div class="mt-12"></div>
      </div>
      <WebsiteFooter />
   </div>
   <div
      v-if="user.site_settings?.has_subscription_free_access"
      class="pointer-events-none fixed bottom-1 left-1 rounded-md bg-emerald-700 p-1 text-xs text-white opacity-75">
      You have access through admin provided free subscription
   </div>


   <!-- Global Subscription CTA Modal -->
   <Dialog
      :visible="isSubscriptionCtaModalOpen"
      @update:visible="(value) => { if (!value) uiStore.closeSubscriptionCtaModal() }"
      modal
      :header="modalContent.title"
      :style="{ width: '25rem' }">
      <div>
         <p>{{ modalContent.message }}</p>
         <NuxtTypedLink
            class="mt-3 flex justify-center rounded-md bg-brand-title p-3 text-center font-semibold text-white"
            :to="{ name: LmsRouteNames.subscribe }"
            @click="uiStore.closeSubscriptionCtaModal()">
            {{ modalContent.buttonText }}
         </NuxtTypedLink>
      </div>
   </Dialog>

   <!-- Contact Modals (Register Interest, Contributor Contact) -->
   <ContactModals />
</template>

<style scoped></style>
